import { useContext } from 'react'
import type { MouseEvent } from 'react'
import type { AppDispatch } from '@redux/store'

import { deepSetObject, mergeAndStringifyEntityObjects } from '@domain-group/javascript-utils'
import { useForm, useLatestEntity, useSetState } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as attachmentActions from '@redux/modules/attachment'

import defaultFormState, { requiredFields } from '@models/attachment'

import PageContext from '@contexts/pageContext'

import type { DeleteParams, EntityOptions } from '@redux/modules/types'
import type { AttachmentModel, AttachmentTag } from '@domain-group/realhub-backend-api-types'

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

export const generateUrls = (attachment?: Partial<AttachmentModel>) => {
  const { id } = attachment || {}

  return {
    editAttachmentDetailsUrl: `#/sectionName/attachments/${id}/edit/details`,
    editAttachmentUrl: `#/sectionName/attachments/${id}/edit/`,
    attachmentsIndexUrl: '#/sectionName/attachments',
  }
}

type CreateAttachmentParams = {
  dispatch: AppDispatch,
  attachmentParams: Partial<AttachmentModel>,
  requestOptions?: EntityOptions,
}

const createAttachment = (params: CreateAttachmentParams) => {
  const { attachmentParams, dispatch, requestOptions } = params
  const { createAttachment: createFn } = attachmentActions

  return dispatch(createFn(attachmentParams, requestOptions))
}

type UpdateAttachmentParams = {
  dispatch: AppDispatch,
  attachment: Partial<AttachmentModel>,
  attachmentParams: Partial<AttachmentModel>,
  requestOptions?: EntityOptions,
}

const updateAttachment = (params: UpdateAttachmentParams) => {
  const {
    dispatch, attachment, attachmentParams, requestOptions,
  } = params
  const { updateAttachment: updateFn } = attachmentActions

  const updatedParams = mergeAndStringifyEntityObjects(attachment, attachmentParams, ['data'])

  // @ts-expect-error TS2345
  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteAttachmentParams = {
  attachment: DeleteParams<AttachmentModel>,
  dispatch: AppDispatch,
}

const deleteAttachment = (params: DeleteAttachmentParams) => {
  const { attachment, dispatch } = params
  const { deleteAttachment: deleteFn } = attachmentActions

  return dispatch(deleteFn(attachment))
}

type ToggleAttachmentResolutionParams = {
  attachment: Partial<AttachmentModel>,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
  tag: AttachmentTag,
}

const toggleAttachmentResolution = (params: ToggleAttachmentResolutionParams) => {
  const {
    attachment, dispatch, requestOptions, tag,
  } = params

  const updatedTag = {
    ...tag,
    resolved: !tag.resolved,
  }

  // @ts-expect-error
  const tags = Array.from(attachment?.data?.tags ?? [])
  // @ts-expect-error TS18046
  const tagIndex = tags.findIndex(t => t.id === updatedTag.id)

  tags.splice(tagIndex, 1, updatedTag)

  const updatedAttachment = deepSetObject(attachment, 'data.tags', tags)

  return updateAttachment({
    attachment,
    // @ts-expect-error TS2322
    attachmentParams: updatedAttachment,
    dispatch,
    requestOptions,
  })
}

type DownloadAttachmentParams = {
  e: any,
  attachment: Partial<AttachmentModel>,
  dispatch: AppDispatch,
  setState: (state: Partial<typeof defaultState>) => void,
}

const downloadAttachment = (params: DownloadAttachmentParams) => {
  const {
    e, attachment, setState, dispatch,
  } = params

  if (e) e.stopPropagation()

  const { generateAttachmentUrl } = attachmentActions

  setState({ generatingUrl: true })

  // @ts-expect-error TS2345
  dispatch(generateAttachmentUrl(attachment.id))
    // @ts-expect-error TS2345
    .then(({ success, data }: { success: boolean, data: { links: { force_attachment: string } } }) => {
      setState({ generatingUrl: false })

      if (success && data.links?.force_attachment){
        window.location.href = data.links.force_attachment
      }
    })
}

export type ShowCreateOrEditAttachmentModalPayload = {
  attachment: Partial<AttachmentModel>,
}

type CreateOrEditAttachmentParams = {
  attachment: Partial<AttachmentModel>,
  showCreateOrEditAttachmentModal: (payload: ShowCreateOrEditAttachmentModalPayload) => void,
}

const createOrEditAttachment = (params: CreateOrEditAttachmentParams) => new Promise((resolve, reject) => {
  const { attachment, showCreateOrEditAttachmentModal } = params

  if (showCreateOrEditAttachmentModal){
    const payload = {
      attachment,
    }

    showCreateOrEditAttachmentModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateOrEditAttachmentModal not defined in PageContext callbacks'))
})

export type UploadEntityAssetsModalPayload = {
  attachment: Partial<AttachmentModel>,
}

type UploadAttachmentParams = {
  attachment: Partial<AttachmentModel>,
  showUploadEntityAssetsModal: (payload: UploadEntityAssetsModalPayload) => void,
  customPayload?: object,
}

const uploadAttachment = (params: UploadAttachmentParams) => (
  new Promise((resolve, reject) => {
    const { attachment, showUploadEntityAssetsModal, customPayload = {} } = params

    if (showUploadEntityAssetsModal){
      const payload = {
        attachment,
        ...customPayload,
      }

      showUploadEntityAssetsModal(payload)

      resolve({ success: true, result: payload })
    }

    reject(new Error('showUploadEntityAssetsModal not defined in PageContext callbacks'))
  })
)

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useAttachmentForm(
  attachment: Partial<AttachmentModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const attachmentForm = useForm(
    defaultFormState,
    { entity: attachment, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [attachment.id, attachment.cache_key],
  )

  return {
    ...attachmentForm,
  }
}

// Return related entities
export const useRelations = (entity: Partial<AttachmentModel> = {}) => {
  const { attachable_id, attachable_type } = entity

  const { taskListItems } = useSelector(reduxState => reduxState.entities)

  const taskListItem = attachable_type === 'TaskListItem' && attachable_id && taskListItems[attachable_id]
    ? taskListItems[attachable_id]
    : {}

  return {
    taskListItem,
  }
}

const defaultState = {
  generatingUrl: false,
}

// @ts-expect-error TS1015
function useAttachment(initEntity: Partial<AttachmentModel> = {}, options?: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  const [state, setState] = useSetState(defaultState)
  const { generatingUrl } = state

  const { entity: attachment }: { entity: Partial<AttachmentModel>} = useLatestEntity(initEntity, 'attachments')
  const { id } = attachment

  const dispatch = useDispatch()

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      showCreateOrEditAttachmentModal,
      showUploadEntityAssetsModal,
    },
  } = pageContext

  useReduxAction(
    'attachments',
    'loadEntity',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.attachments)

  // @ts-expect-error
  const tags: AttachmentTag[] = attachment?.data?.tags ?? []

  return {
    callbacks: {
      createAttachment: (attachmentParams: Partial<AttachmentModel>, entityOptions?: EntityOptions) => (
        createAttachment({ attachmentParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditAttachment: () => createOrEditAttachment({ attachment, showCreateOrEditAttachmentModal }),
      // @ts-expect-error TS2322
      deleteAttachment: () => deleteAttachment({ dispatch, attachment }),
      downloadAttachment: (e: MouseEvent) => downloadAttachment({
        e, attachment, setState, dispatch,
      }),
      toggleAttachmentResolution: (tag: AttachmentTag, entityOptions?: EntityOptions) => (
        toggleAttachmentResolution({
          attachment, dispatch, requestOptions: entityOptions, tag,
        })
      ),
      updateAttachment: (attachmentParams: Partial<AttachmentModel>, entityOptions?: EntityOptions) => (
        updateAttachment({
          attachment, attachmentParams, dispatch, requestOptions: entityOptions,
        })
      ),
      uploadAttachment: (customPayload?: object) => (
        uploadAttachment({ attachment, showUploadEntityAssetsModal, customPayload })
      ),
    },
    attachment,
    creating,
    deleting,
    generatingUrl,
    loading,
    tags,
    updating,
    urls: generateUrls(attachment),
  }
}

export default useAttachment
